import { createElement as rc, useState, useEffect, useRef, useCallback, useContext } from 'react';
import lodash from 'lodash';
const { omit, cloneDeep } = lodash;
import { h3, Button, testProperties, sorts, contexts } from 'lib_ui-primitives';
import DynamicFieldEntryRow from './DynamicFieldEntryRow';
import { metadata } from 'lib_ui-services';
import createTreePositionGetter from '../../../utilities/createTreePositionGetter';
import { ReverseEntries, StyledDynamicFieldEntry, Header, Title } from './styles';
import useEventSink from '../../../hooks/useEventSink';
import useActiveRecord from '../../../hooks/useActiveRecord';
import useExternalFieldAddAndRemoveRequests from './useExternalFieldAddAndRemoveRequests';
import cuid from 'cuid';

const _p = {
    getFields
};

export const _private = _p;
/**
 * This component allows a user to select from a dropdown, at runtime, what type of
 * information (i.e. hNodeType) they would like to edit for a given relation (e.g. item:item).
 * That dropdown is then transformed into an input control for the selected hNodeType so
 * the user can actually enter a new value using that input control.
 *
 * It can be used for things like mass updates.
 * e.g.
 * 1) A user selectes "Asset Type" from the initial dropdown.
 * 2) The dropdown is transformed into another dropdown containing asset types.
 * 3) The user selects the desired asset type from that dropdown.
 * 4) The user scans a bunch of assets, changing them all to have the selected asset type.
 *
 * A plus button is provided, allowing multiple entries to be added (so that multiple types
 * of data can be edited at the same time).
 *
 * The list of valid fields for editing is based on the data dictionary for the
 * component's namespace/relation.
 */
export default function DynamicFieldEntry(props) {
    const {
        hNode,
        hNode: { foreignNamespace, foreignRelation, excludedFields = [], treePosition },
        currentRoute,
        entries,
        setEntries,
        entriesReady
    } = props ?? { hNode: {} };
    const { formInstanceId } = useActiveRecord() || {};
    const focusContext = useContext(contexts.FocusContext);
    const [, publish] = useEventSink();
    const { title } = props;

    // The types of fields (i.e. hNodes) that can be used. These are
    // rendered in a dropdown of an entry when the user first adds
    // a new entry.
    const [allowedEntries, _setAllowedEntries] = useState([]);
    // Entries represent either new rows in the component for which the
    // user has not already selected an allowed value field (i.e. hNode)
    // or rows for the field (hNode) and corresponding value the user has
    // supplied for that field.

    const [ready, setReady] = useState(false);

    useEffect(() => {
        focusContext.disableFocusManagement = true;
        focusContext.reportChanges = false;
        return () => {
            focusContext.disableFocusManagement = false;
            focusContext.reportChanges = true;
        };
    }, [focusContext]);

    // Proxy for original setAllowedEntries which sorts the entries by title before passing
    // the result on to the original setAllowedEntries function.
    const setAllowedEntries = useCallback(newEntries => {
        _setAllowedEntries(prev => {
            let results = newEntries;
            // avoid sorting (and rerendering) if this is the same reference
            if (results === prev) return prev;
            // Handle a function setter
            if (typeof newEntries === 'function') {
                results = newEntries(prev);
                // avoid sorting (and rerendering) if the function resturns the same reference
                if (results === prev) {
                    return prev;
                }
            }
            return sorts.sortByField(results, 'title');
        });
    }, []);

    // If some exteral code (e.g. rules engine) wants to add or remove entries, this will
    // allow that to happen.
    useExternalFieldAddAndRemoveRequests(setEntries, setAllowedEntries, entriesReady);

    // Contains all the possible entries (i.e. hNodes) that can be used in this component's
    // namespace/relation.
    const validEntries = useRef([]);
    useEffect(() => {
        async function doAsync() {
            const fields = await _p.getFields(foreignNamespace, foreignRelation);
            validEntries.current = fields.filter(f => !!f.title);
            setAllowedEntries(fields.filter(f => !!f.title));
            setReady(true);
        }
        doAsync();
    }, [foreignNamespace, foreignRelation, setAllowedEntries, treePosition]);

    // Don't allow the user to add a new entry if there are no more allowed entries (e.g.
    // the user has already selected all of the allowed entries).
    const [addAllowed, setAddAllowed] = useState(false);
    useEffect(() => {
        setAddAllowed(allowedEntries.length > 0 && entries.length < validEntries.current.length);
    }, [entries, allowedEntries]);

    // This tries to move the focus to the first entry that has not been assigned a value
    // yet, but honestly it's not very good at it.  I don't have time to work on improving
    // it right now.
    useEffect(() => {
        if (ready && entriesReady) {
            const firstUnassignedIndex = entries.findIndex(e => !e.hNodeType);
            focusContext.focusIfExists(`normal-dropdown-input-fdd${firstUnassignedIndex}`);
        }
    }, [entries, focusContext, ready, entriesReady]);

    const initialAllowedSet = useRef(false);
    useEffect(() => {
        // One time only, set allowed entries to those that are not in already selected
        // and not explicitly excluded in the blockly excludedFields value.
        if (initialAllowedSet.current === false && entriesReady && ready) {
            const entryIds = entries.map(e => e.id);
            const newAllowed = allowedEntries.filter(
                a => !entryIds.includes(a.id ?? 0) && !excludedFields.includes(metadata.getPathToProperty(a))
            );
            setAllowedEntries(newAllowed.filter(f => !!f.title));
            initialAllowedSet.current = true;
        }
    }, [entriesReady, ready, allowedEntries, entries, excludedFields, setAllowedEntries]);

    // Adds a new entry to the end of the list of entries.  The CSS is set up so that this
    // new entry will render at the top of the list (i.e. the most recent entry is at the top).
    function add() {
        focusContext.reportChanges = true;
        setEntries(prev => {
            return [...prev, { id: cuid() }];
        });
    }

    // Removes the entry at the given index from the list of entries.
    function remove(index, childHNode) {
        focusContext.reportChanges = true;
        setEntries(prev => {
            const newArray = [...prev];
            newArray.splice(index, 1);
            publish(
                { childHNode, entries: newArray },
                { verb: 'update', namespace: 'application', relation: 'runtime', type: 'DynamicFieldEntryRemoved' }
            );
            // Begin handling deletion of removed hNode's data
            publish(
                { hNode: childHNode },
                {
                    verb: 'removedHNode',
                    namespace: 'application',
                    relation: 'runtime',
                    ...(formInstanceId == null ? {} : { formInstanceId })
                }
            );
            return newArray;
        });
        // Put the removed entry back into the allowed entries so it can be reused.
        setAllowedEntries(prev => {
            if (childHNode != null && !prev.includes(childHNode)) {
                return [...prev, childHNode];
            }
            return prev;
        });
    }

    // This is called when the user selects an hNodeType for the entry.
    function entryUsed(index, childHNode) {
        setEntries(prev => {
            const newArray = [...prev];
            newArray[index] = childHNode;
            return newArray;
        });
        setAllowedEntries(prev => {
            if (childHNode != null && prev.some(p => p.id === childHNode.id)) {
                const newValues = [...prev];
                newValues.splice(
                    newValues.findIndex(n => n.id === childHNode.id),
                    1
                );
                return newValues;
            }
            return prev;
        });

        // Inform the rest of the application that a new entry field is ready to be used.
        publish(
            { childHNode, allowedEntries },
            { verb: 'update', namespace: 'application', relation: 'runtime', type: 'DynamicFieldEntryUsed' }
        );
    }

    if (!ready) {
        return rc(h3, null, 'Loading...');
    }

    const getTreePosition = createTreePositionGetter(hNode.treePosition, entries.length);
    // prettier-ignore
    return rc(StyledDynamicFieldEntry, testProperties(props),
        addAllowed && rc(Header, { key: 'header' },
            rc(Title, null, title?.length > 0 ? title : 'Add a value to update ➟'),
            rc(Button, { ...testProperties(hNode, 'add'), icon: 'add', buttonStyle: 'round', onClick: add })
        ),
        // There seems to be some trouble with the react-reconciler where changing the order of the dom nodes
        // by inserting a new one at the beginning will cause the reconciler to give up and remount the node
        // which means all previous state is lost for that node.  So instead, we use the column-reverse flex
        // direction to order the nodes (with the newest at the top even though it is last in the dom).
        // If you want to see the problem, go to react-dom.development.js and put a breakpoint in the
        // 'updateSlot' method.  You'll see it compares the previous key value using the node that was at that
        // location in the dom with the new key value for the node you inserted.
        rc(ReverseEntries, { key: 'reverse-entries' },
            entries.map((_entry, index) => {
                // avoid editing entry directly
                const entry = cloneDeep(_entry);
                const key = entry.id;
                // Ensure any entries that have an hNode also have a tree position
                // designated.
                if (entry != null) {
                    entry.treePosition = getTreePosition(index);
                }
                return rc(DynamicFieldEntryRow, {
                    name: 'dynamic-field-entry-row',
                    parentHNode: hNode,
                    entry,
                    allowedEntries,
                    index,
                    remove,
                    entryUsed,
                    key,
                    currentRoute
                });
            })
        )
    );
}

/**
 * Gets the hNodes for all formElements in the data dictionary which are used
 * to write data to the given namespace and relation.
 * @param {string} namespace
 * @param {string} relation
 * @returns [hNodes]
 */
async function getFields(namespace, relation) {
    const dictionary = await metadata.getDictionary();
    const values = omit(dictionary[namespace][relation], '_meta');
    const fields = Object.values(values).map(value => value._meta);
    fields.sort((a, b) => (a.title > b.title ? 1 : -1));
    return fields;
}
